// Country resolution for the dashboard.
//
// The country comes from the first URL segment (/sa/..., /kw/...), is fixed for
// the lifetime of the page (switching country is a full-page navigation) and is
// used as the router basename and the X-Country API header.
//
// The backend keeps ONE database with shared users: the same account and token
// work in every country, so the auth storage keys are NOT scoped per country.

import type { Country, CountryCode } from '@configs/countries'
import { COUNTRIES, COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from '@configs/countries'

export const COUNTRY_HEADER = 'X-Country'

let currentCountry: Country | null = null

const countryCodeFromSegment = (segment: string | undefined): CountryCode | null => {
  const code = (segment || '').toUpperCase() as CountryCode

  return COUNTRY_CODES.includes(code) ? code : null
}

/**
 * Make sure the URL starts with a country prefix before the router reads it:
 * `/`, `/en/login` etc. become `/sa/`, `/sa/en/login` (Saudi Arabia is the
 * default country, and the pre-multi-country URLs all belonged to it).
 */
const ensureCountryInUrl = (): CountryCode => {
  const { pathname, search, hash } = window.location
  const [, firstSegment, ...rest] = pathname.split('/')
  const code = countryCodeFromSegment(firstSegment)

  if (code) {
    // Normalise /SA/... to /sa/...
    if (firstSegment !== code.toLowerCase()) {
      window.history.replaceState(
        window.history.state,
        '',
        `/${[code.toLowerCase(), ...rest].join('/')}${search}${hash}`
      )
    }

    return code
  }

  window.history.replaceState(
    window.history.state,
    '',
    `/${DEFAULT_COUNTRY_CODE.toLowerCase()}${pathname === '/' ? '/' : pathname}${search}${hash}`
  )

  return DEFAULT_COUNTRY_CODE
}

export const getCurrentCountry = (): Country => {
  if (!currentCountry) {
    currentCountry = { ...COUNTRIES[ensureCountryInUrl()] }
  }

  return currentCountry
}

export const getCountryCode = (): CountryCode => getCurrentCountry().code

// Router basename, e.g. `/sa`
export const getCountryBasename = (): string => `/${getCountryCode().toLowerCase()}`

// Absolute path for hard navigations (window.location), e.g. `/sa/en/login`
export const countryPath = (path: string): string =>
  `${getCountryBasename()}${path.startsWith('/') ? path : `/${path}`}`

/**
 * An earlier build gave every country its own database, so it stored the auth
 * data under country-suffixed keys (`accessToken_sa`). Users are shared again:
 * move the data back to the un-suffixed keys once — preferring the session of
 * the country in the URL — and drop the suffixed copies. Nobody is logged out.
 */
export const migrateCountryScopedStorage = (keys: readonly string[]): void => {
  try {
    // The country in the URL first, so its session wins when several exist
    const codes = [getCountryCode(), ...COUNTRY_CODES.filter(code => code !== getCountryCode())]

    keys.forEach(key => {
      if (localStorage.getItem(key) === null) {
        for (const code of codes) {
          const scoped = localStorage.getItem(`${key}_${code.toLowerCase()}`)

          if (scoped !== null) {
            localStorage.setItem(key, scoped)
            break
          }
        }
      }

      COUNTRY_CODES.forEach(code => localStorage.removeItem(`${key}_${code.toLowerCase()}`))
    })
  } catch {
    // Storage unavailable (private mode etc.) — nothing to migrate
  }
}

/**
 * Refresh the static country settings (currency, dial code) from the backend's
 * public `GET /api/v1/store/country`. Never blocks rendering; the static config
 * is used until (and if) the request succeeds.
 */
export const syncCountryFromApi = async (fetchCountry: () => Promise<any>): Promise<void> => {
  try {
    const remote = await fetchCountry()
    const country = getCurrentCountry()

    if (!remote || String(remote.code || '').toUpperCase() !== country.code) return

    if (remote.dial_code) country.dial_code = String(remote.dial_code)
    if (Number(remote.mobile_length) > 0) country.mobile_length = Number(remote.mobile_length)

    if (remote.currency?.code) {
      country.currency = {
        code: String(remote.currency.code),
        symbol_en: String(remote.currency.symbol_en || country.currency.symbol_en),
        symbol_ar: String(remote.currency.symbol_ar || country.currency.symbol_ar),
        decimals: Number.isFinite(Number(remote.currency.decimals))
          ? Number(remote.currency.decimals)
          : country.currency.decimals
      }
    }
  } catch {
    // Keep the static config
  }
}
